import {
  ChangeDetectorRef,
  Component,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Navigation, ParamMap, Router } from '@angular/router';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
  AllExports,
  CiscoMigrationSite,
  CiscoPbxExport,
  MigrationStatusV2,
  NumberReportEntry,
  PbxFields,
  RingCentralExportDetails,
} from '../../../shared/models/generated/ziroModels';
import { DatatableColumn, DatatableRow, SortOrder } from '../../../shared/tables/datatable/datatable.models';
import { DatatableComponent } from '../../../shared/tables/datatable/datatable.component';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { MigrationsCucmNumberReportContext } from '../../services/cisco/migrations-cucm-number-report.context';
import { MigrationsNumberContext, PbxType } from '../../services/migrations-numbers-context.service';
import { MigrationsCiscoSiteManagementContext } from '../migrations-cisco-site-management/migrations-cisco-site-management.context';
import {
  MigrationsSiteProgressService,
  SiteProgress,
  SiteProgressBucket,
  SiteProgressReport,
  SiteStatusSegment,
} from '../../services/migrations-site-progress.service';
import {
  MigrationsSitesOverviewStateService,
  SiteBarMode,
  SitesOverviewState,
} from './migrations-sites-overview-state.service';
import { MigrationsService } from '../../services/migrations.service';
import { MigrationsAssessmentsContext } from '../../services/migrations-assessments.context';

export interface SiteOverviewRow extends DatatableRow {
  id: string;
  siteId: string;
  siteName: string;
  progressPercent: number;
  totalDirectoryNumbers: number;
  progress: SiteProgress;
}

export interface SitesOverviewFilterChip {
  key: string;
  label: string;
  color: string;
}

export interface SitesOverviewStatusOption {
  status: MigrationStatusV2;
  label: string;
  color: string;
  isActive: boolean;
}

@Component({
  selector: 'ziro-migrations-sites-overview',
  templateUrl: './migrations-sites-overview.component.html',
  styleUrls: ['../../migrations.component.scss', './migrations-sites-overview.component.scss'],
  standalone: false,
  providers: [MigrationsSiteProgressService, MigrationsService, MigrationsCiscoSiteManagementContext],
})
export class MigrationsSitesOverviewComponent implements OnInit, OnDestroy {
  ZiroIcons = ZiroIcons;
  ButtonStyles = ButtonStyles;
  SiteBarMode = SiteBarMode;

  isLoading: boolean = true;
  rows: SiteOverviewRow[] = [];
  chips: SitesOverviewFilterChip[] = [];
  statusFilterOptions: SitesOverviewStatusOption[] = [];

  columns: DatatableColumn<SiteOverviewRow>[] = [
    {
      name: 'siteName',
      label: 'tkey;migrations.sites_overview.table.site.label',
      columnCharacterWidth: 25,
      sortFn: (first: string, second: string): number => first.localeCompare(second),
    },
    {
      name: 'progressPercent',
      label: 'tkey;migrations.sites_overview.table.progress.label',
      columnCharacterWidth: 12,
      sortFn: (first: number, second: number): number => first - second,
      pipe: {
        transform: (value: number, row: SiteOverviewRow): string => {
          if (row && row.progress.bucket === SiteProgressBucket.NO_ELIGIBLE_DIRECTORY_NUMBERS) {
            return this._translateService.instant('tkey;migrations.sites_overview.table.progress.not_applicable');
          }
          return value > 0 && value < 1 ? '<1%' : `${Math.floor(value)}%`;
        },
      },
      pipeArgs: 'row',
    },
    {
      name: 'totalDirectoryNumbers',
      label: 'tkey;migrations.sites_overview.table.directory_numbers.label',
      columnCharacterWidth: 14,
      sortFn: (first: number, second: number): number => first - second,
    },
    {
      name: 'progress',
      label: 'tkey;migrations.sites_overview.table.breakdown.label',
      columnCharacterWidth: 60,
      cellTemplate: (): TemplateRef<unknown> => this.siteBarCellTemplate,
      hideSort: true,
      disableTooltip: true,
      sortFn: (first: SiteProgress, second: SiteProgress): number => first.progressPercent - second.progressPercent,
    },
  ];

  @ViewChild('datatable') datatable: DatatableComponent<SiteOverviewRow>;
  @ViewChild('siteBarCell', { static: true }) siteBarCellTemplate: TemplateRef<unknown>;

  private _route: ActivatedRoute = inject(ActivatedRoute);
  private _router: Router = inject(Router);
  private _translateService: TranslateService = inject(TranslateService);
  private _bottomNavService: BottomNavService = inject(BottomNavService);
  private _breadcrumbsService: BreadcrumbsService = inject(BreadcrumbsService);
  private _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _siteProgressService: MigrationsSiteProgressService = inject(MigrationsSiteProgressService);
  private _stateService: MigrationsSitesOverviewStateService = inject(MigrationsSitesOverviewStateService);
  private _injector: Injector = inject(Injector);
  private _migrationsAssessmentsContext: MigrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _pbxType: PbxType = this._route.snapshot.data.pbxType;
  private _arrivingNavigation: Navigation = this._router.currentNavigation() ?? this._router.lastSuccessfulNavigation();
  private _previousUrl: string = this._arrivingNavigation?.previousNavigation?.finalUrl?.toString() ?? '';

  private _refreshButtonId: string = 'migrations-sites-overview-refresh';
  private _subscription: Subscription = new Subscription();
  private _assessmentId: string;
  private _assessmentName: string = '';
  private _tenantId: string;
  private _largestSiteDirectoryNumberCount: number = 0;
  private _allSites: SiteProgress[] = [];
  private _state: SitesOverviewState;

  ngOnInit(): void {
    this._tenantId = this._route.snapshot.params.tenantId;
    this._assessmentId = this._route.snapshot.params.id;
    this._state = this._stateService.getState(this._assessmentId);
    if (!this._previousUrl.includes('numbers-report') && !this._previousUrl.includes('sites-overview')) {
      this._patchState({ statusFilter: null });
    }

    this._subscription.add(
      this._route.paramMap.subscribe((parameters: ParamMap): void => {
        const bucketParameter: string = parameters.get('bucket');
        this._patchState({
          bucketFilter: bucketParameter
            ? this._siteProgressService.bucketFromLabel(decodeURIComponent(bucketParameter))
            : null,
        });
        this._buildStatusFilterOptions();
        this._applyFilters();
      }),
    );

    this._initBreadcrumbs();
    this._initBottomNav();
    this._initAssessmentName();
    this._loadData();
  }

  ngOnDestroy(): void {
    this._captureSort();
    this._subscription.unsubscribe();
  }

  private _isCisco(): boolean {
    return this._pbxType === 'cisco';
  }

  getBarMode(): SiteBarMode {
    return this._state.barMode;
  }

  onBarModeChange(mode: SiteBarMode): void {
    this._patchState({ barMode: mode });
  }

  onSegmentClick(row: SiteOverviewRow, segment: SiteStatusSegment, event: MouseEvent): void {
    event.stopPropagation();
    this._captureSort();
    this._navigateToNumberReport(row.siteName, segment.status);
  }

  onRowBarClick(row: SiteOverviewRow): void {
    this._captureSort();
    this._navigateToNumberReport(row.siteName, this._state.statusFilter);
  }

  onStatusFilterClick(status: MigrationStatusV2): void {
    this._patchState({ statusFilter: this._state.statusFilter === status ? null : status });
    this._buildStatusFilterOptions();
    this._applyFilters();
  }

  onChipRemoved(chip: SitesOverviewFilterChip): void {
    if (chip.key === 'bucket') {
      this._router.navigate(['../'], { relativeTo: this._route, replaceUrl: true });
      return;
    }

    this._patchState({ statusFilter: null });
    this._buildStatusFilterOptions();
    this._applyFilters();
  }

  getBarWidthPercent(row: SiteOverviewRow): number {
    if (this._state.barMode === SiteBarMode.FULL_WIDTH || this._largestSiteDirectoryNumberCount <= 0) {
      return 100;
    }
    return Math.max((row.totalDirectoryNumbers / this._largestSiteDirectoryNumberCount) * 100, 1);
  }

  getSegmentTooltip(row: SiteOverviewRow, segment: SiteStatusSegment): string {
    return this._translateService.instant('tkey;migrations.sites_overview.bar.segment.tooltip', {
      count: segment.count,
      status: segment.label,
      site: row.siteName,
    });
  }

  private _navigateToNumberReport(siteName: string, status: MigrationStatusV2): void {
    const reportPath: string = this._isCisco() ? 'cucm-directory-numbers-report' : 'numbers-report';
    const filters: string[] = [`site=${encodeURIComponent(siteName)}`];

    if (status) {
      const statusLabel: string = MigrationsService.MIGRATION_STATUS_ROW_MAP.get(status).label;
      filters.push(`migrationStatus=${encodeURIComponent(statusLabel)}`);
    }

    this._router.navigate(
      [this._route.snapshot.paramMap.has('bucket') ? '../../' : '../', reportPath, filters.join('&')],
      { relativeTo: this._route },
    );
  }

  private _loadData(): void {
    const report$: Observable<{ entries: NumberReportEntry<PbxFields>[] }> = this._isCisco()
      ? this._injector.get(MigrationsCucmNumberReportContext).state$
      : this._injector.get(MigrationsNumberContext).state$;

    const sites$: Observable<CiscoMigrationSite[]> = this._isCisco()
      ? this._injector.get(MigrationsCiscoSiteManagementContext).state$.pipe(startWith([]))
      : of([]);

    const subscription: Subscription = combineLatest([report$, sites$])
      .pipe(
        map(([report, sites]): SiteProgressReport => {
          const siteNamesBySiteId: Map<string, string> = new Map(
            sites.map((site: CiscoMigrationSite): [string, string] => [site.siteId, site.name]),
          );
          return this._siteProgressService.aggregate(report.entries, siteNamesBySiteId);
        }),
      )
      .subscribe((progressReport: SiteProgressReport): void => {
        this._allSites = progressReport.sites;
        this._largestSiteDirectoryNumberCount = this._allSites.reduce(
          (largest: number, site: SiteProgress): number => Math.max(largest, site.totalDirectoryNumbers),
          0,
        );
        this.isLoading = false;
        this._applyFilters();
        this._restoreSort();
      });

    this._subscription.add(subscription);
  }

  private _applyFilters(): void {
    this.rows = this._allSites
      .filter((site: SiteProgress): boolean => !this._state.bucketFilter || site.bucket === this._state.bucketFilter)
      .filter(
        (site: SiteProgress): boolean =>
          !this._state.statusFilter ||
          site.segments.some((segment: SiteStatusSegment): boolean => segment.status === this._state.statusFilter),
      )
      .map(
        (site: SiteProgress): SiteOverviewRow => ({
          id: site.siteId,
          siteId: site.siteId,
          siteName: site.siteName,
          progressPercent: site.progressPercent,
          totalDirectoryNumbers: site.totalDirectoryNumbers,
          progress: site,
        }),
      );

    this._buildChips();
    this._changeDetectorRef.detectChanges();
  }

  private _buildStatusFilterOptions(): void {
    this.statusFilterOptions = Array.from(MigrationsService.MIGRATION_STATUS_ROW_MAP.entries()).map(
      ([status, statusRow]): SitesOverviewStatusOption => ({
        status,
        label: statusRow.label,
        color: statusRow.iconColor,
        isActive: this._state.statusFilter === status,
      }),
    );
  }

  private _buildChips(): void {
    const chips: SitesOverviewFilterChip[] = [];

    if (this._state.bucketFilter) {
      chips.push({
        key: 'bucket',
        label: this._siteProgressService.labelFor(this._state.bucketFilter),
        color: MigrationsSiteProgressService.colorFor(this._state.bucketFilter),
      });
    }

    if (this._state.statusFilter) {
      const statusRow = MigrationsService.MIGRATION_STATUS_ROW_MAP.get(this._state.statusFilter);
      chips.push({ key: 'status', label: statusRow.label, color: statusRow.iconColor });
    }

    this.chips = chips;
  }

  private _patchState(changes: Partial<SitesOverviewState>): void {
    this._state = { ...this._state, ...changes };
    this._stateService.patchState(this._assessmentId, changes);
  }

  private _captureSort(): void {
    if (this.datatable && this.datatable.sortActive) {
      this._patchState({ sortColumn: this.datatable.sortActive, sortOrder: this.datatable.sortState });
    }
  }

  private _restoreSort(): void {
    if (!this._state.sortColumn || !this._state.sortOrder) {
      return;
    }

    const column: DatatableColumn<SiteOverviewRow> = this.columns.find(
      (candidate: DatatableColumn<SiteOverviewRow>): boolean => candidate.name === this._state.sortColumn,
    );
    if (!column) {
      return;
    }

    setTimeout((): void => {
      const toggleFrom: SortOrder = this._state.sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      if (this.datatable) {
        this.datatable.sort(column.name, toggleFrom, false);
        this._changeDetectorRef.detectChanges();
      }
    });
  }

  private _initAssessmentName(): void {
    const subscription: Subscription = this._migrationsAssessmentsContext.state$.subscribe(
      (allExports: AllExports): void => {
        this._assessmentName = this._isCisco()
          ? (allExports.ciscoPbxExports.find(
              (ciscoExport: CiscoPbxExport): boolean => ciscoExport.id === this._assessmentId,
            )?.name ?? '')
          : (allExports.ringCentralExports.find(
              (ringCentralExport: RingCentralExportDetails): boolean =>
                ringCentralExport.exportId === this._assessmentId,
            )?.name ?? '');
        this._initBreadcrumbs();
      },
    );
    this._subscription.add(subscription);
  }

  private _initBreadcrumbs(): void {
    const pbxSegment: string = this._isCisco() ? 'cisco' : 'ring-central';
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this._tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: this._assessmentName,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/${pbxSegment}/${this._assessmentId}`,
      },
      { label: this._translateService.instant('tkey;migrations.sites_overview.breadcrumb') },
    ]);
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'migrations-sites-overview-back',
          dataAutomation: 'migrations-sites-overview-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: (): void => {
            this._router.navigate([this._route.snapshot.paramMap.has('bucket') ? '../../' : '../'], {
              relativeTo: this._route,
            });
          },
        },
        {
          id: this._refreshButtonId,
          dataAutomation: 'migrations-sites-overview-refresh-button',
          label: 'tkey;migrations.sites_overview.refresh_button.label',
          icon: ZiroIcons.REFRESH,
          buttonClass: ButtonStyles.PRIMARY,
          state: { pending: false, buttonDisableState: { disabled: false } },
          cb: (): void => this._refresh(),
        },
      ]),
    );
  }

  private _refresh(): void {
    this._captureSort();
    this.isLoading = true;
    const refresh$: Observable<void> = this._isCisco()
      ? this._injector.get(MigrationsCucmNumberReportContext).refresh()
      : this._injector.get(MigrationsNumberContext).refresh();

    this._subscription.add(
      refresh$.subscribe((): void => {
        this.isLoading = false;
      }),
    );
  }
}
